import React from 'react';
import { BarChart as RechartsBarChart, Bar, Cell, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, ReferenceLine } from 'recharts';
import { COLORS, formatYAxisNumber } from '../utils/ChartConstants';
import { CustomTooltip } from '../utils/CustomTooltip';

interface BarChartProps {
  data: any[];
  originalChart?: any;
}

// Custom bar shape with rounded corners (top for positive, bottom for negative)
const RoundedBar = (props: any) => {
  const { fill, x, y, width, height, value, payload, background } = props;
  const radius = 8;

  // Get the actual value - try multiple sources
  let actualValue = 0;
  if (value !== undefined && value !== null) {
    actualValue = value;
  } else if (payload) {
    // Try to get value from various payload properties
    actualValue = payload.value ?? payload[Object.keys(payload).find(k => k !== 'name' && typeof payload[k] === 'number') || ''] ?? 0;
  }

  // For negative values, round the bottom corners; for positive, round the top
  const isNegative = actualValue < 0;

  let path;
  if (isNegative) {
    // For negative bars:
    // - y is where the negative value is (should have CURVES)
    // - y + height is the baseline/0 (should be FLAT)
    const baselineY = y + height; // The baseline (0) position
    const curveStart = y - radius; // Where curve starts at the negative value

    // Start at baseline (flat), go down to negative value (curves)
    path = `
      M ${x},${baselineY}
      L ${x},${curveStart}
      Q ${x},${y} ${x + radius},${y}
      L ${x + width - radius},${y}
      Q ${x + width},${y} ${x + width},${curveStart}
      L ${x + width},${baselineY}
      Z
    `;
  } else {
    // Round top corners for positive bars (at y, the top)
    path = `
      M ${x},${y + height}
      L ${x},${y + radius}
      Q ${x},${y} ${x + radius},${y}
      L ${x + width - radius},${y}
      Q ${x + width},${y} ${x + width},${y + radius}
      L ${x + width},${y + height}
      Z
    `;
  }

  return <path d={path} fill={fill} />;
};

// Custom tick component that wraps text or tilts based on width
const CustomWrappedTick = (props: any) => {
  const { x, y, payload, width, shouldTilt } = props;

  if (!payload?.value) return null;

  const text = String(payload.value);

  // If shouldTilt is true, tilt all labels with wrapping
  if (shouldTilt) {
    const fontSize = 11;
    const maxCharsPerLine = 20; // Maximum characters per line for tilted text

    // Split text into lines
    const words = text.split(' ');
    const lines: string[] = [];
    let currentLine = '';

    for (const word of words) {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (testLine.length <= maxCharsPerLine) {
        currentLine = testLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
    if (currentLine) lines.push(currentLine);

    // Limit to 2-3 lines for tilted text
    const displayLines = lines.slice(0, 3);

    return (
      <text
        x={x}
        y={y}
        textAnchor="end"
        fill="#666"
        fontSize={fontSize}
        transform={`rotate(-45 ${x} ${y})`}
      >
        {displayLines.map((line, index) => (
          <tspan
            key={index}
            x={x}
            dy={index === 0 ? '0.71em' : '1.1em'}
            dx="-0.5em"
          >
            {line}
          </tspan>
        ))}
      </text>
    );
  }

  // Otherwise, wrap text within bar width
  const barWidth = width || 60;
  const fontSize = 12;
  const charWidth = fontSize * 0.55;
  const maxCharsPerLine = Math.floor((barWidth - 4) / charWidth);

  // Function to break word with hyphen if needed
  const breakWord = (word: string, maxLength: number): string[] => {
    if (word.length <= maxLength) return [word];

    const parts: string[] = [];
    let remaining = word;

    while (remaining.length > 0) {
      if (remaining.length <= maxLength) {
        parts.push(remaining);
        break;
      }
      const breakPoint = maxLength - 1;
      parts.push(remaining.substring(0, breakPoint) + '-');
      remaining = remaining.substring(breakPoint);
    }

    return parts;
  };

  // Process text into lines
  const lines: string[] = [];
  const words = text.split(' ');
  let currentLine = '';

  for (const word of words) {
    if (word.length > maxCharsPerLine) {
      if (currentLine) {
        lines.push(currentLine);
        currentLine = '';
      }
      const brokenWords = breakWord(word, maxCharsPerLine);
      lines.push(...brokenWords);
    } else {
      const testLine = currentLine ? `${currentLine} ${word}` : word;
      if (testLine.length <= maxCharsPerLine) {
        currentLine = testLine;
      } else {
        if (currentLine) lines.push(currentLine);
        currentLine = word;
      }
    }
  }
  if (currentLine) lines.push(currentLine);

  // Limit to max 3 lines
  const displayLines = lines.slice(0, 3);
  if (lines.length > 3) {
    const lastLine = displayLines[2];
    if (lastLine.length > maxCharsPerLine - 3) {
      displayLines[2] = lastLine.substring(0, maxCharsPerLine - 3) + '...';
    } else {
      displayLines[2] = lastLine + '...';
    }
  }

  return (
    <text
      x={x}
      y={y}
      textAnchor="middle"
      fill="#666"
      fontSize={fontSize}
    >
      {displayLines.map((line, index) => (
        <tspan
          key={index}
          x={x}
          dy={index === 0 ? '0.71em' : '1.2em'}
        >
          {line}
        </tspan>
      ))}
    </text>
  );
};

export const BarChart: React.FC<BarChartProps> = ({ data, originalChart }) => {
  // Calculate approximate bar width based on data count
  // Use a more conservative estimate for small screens
  const estimatedBarWidth = Math.max(30, 300 / data.length);

  // Check if any label would overflow when wrapped
  // Use a more conservative check - tilt if bars are narrow or text is long
  const shouldTiltLabels = React.useMemo(() => {
    // Always tilt if we have more than 3 data points or bar width is less than 100px
    if (data.length > 3 || estimatedBarWidth < 100) return true;

    const fontSize = 12;
    const charWidth = fontSize * 0.55;
    const maxCharsPerLine = Math.floor((estimatedBarWidth - 4) / charWidth);

    return data.some(item => {
      if (!item.name) return false;
      const text = String(item.name);

      // Check if text is too long overall or any word is too long
      if (text.length > maxCharsPerLine * 2) return true;

      const words = text.split(' ');
      return words.some(word => word.length > maxCharsPerLine);
    });
  }, [data, estimatedBarWidth]);

  const bottomMargin = shouldTiltLabels ? 60 : 0;

  // Check if this is a stacked bar chart with multiple datasets
  const isStacked = originalChart?.chart_config?.stacked === true;

  // Extract dataset keys from original chart or transformed data
  const datasetKeys = React.useMemo(() => {
    if (originalChart?.datasets && originalChart.datasets.length > 1) {
      // Use labels from original datasets
      return originalChart.datasets.map((ds: any) => ds.label || 'value');
    } else if (data.length > 0) {
      // Fallback: Get all keys from first data item except 'name' and non-data properties
      const keys = Object.keys(data[0]).filter(key =>
        key !== 'name' &&
        key !== 'fill' &&
        key !== 'color' &&
        key !== 'stroke'
      );
      // If we found multiple keys, it's likely a multi-dataset chart
      return keys.length > 0 ? keys : ['value'];
    }
    return ['value'];
  }, [data, originalChart]);

  // Determine if we have multiple datasets based on datasetKeys or originalChart
  const hasMultipleDatasets = datasetKeys.length > 1;

  // Define colors for each dataset - using COLORS from ChartConstants
  const datasetColors = COLORS;

  // Check if data contains negative values
  const hasNegativeValues = React.useMemo(() => {
    return data.some(item => {
      // Check all numeric properties except 'name'
      return Object.keys(item).some(key => {
        if (key === 'name' || key === 'fill' || key === 'color') return false;
        return typeof item[key] === 'number' && item[key] < 0;
      });
    });
  }, [data]);

  // Calculate Y-axis domain with padding
  const yAxisDomain = React.useMemo(() => {
    if (originalChart?.chart_config?.y_domain) {
      return originalChart.chart_config.y_domain;
    }

    // Calculate min and max values from data
    let dataMin = 0;
    let dataMax = 0;

    data.forEach(item => {
      Object.keys(item).forEach(key => {
        if (key !== 'name' && key !== 'fill' && key !== 'color' && key !== 'stroke') {
          const value = item[key];
          if (typeof value === 'number') {
            if (value < dataMin) dataMin = value;
            if (value > dataMax) dataMax = value;
          }
        }
      });
    });

    // Add padding: ceil(max) + 1 for top, floor(min) - 1 for bottom (if negative)
    const paddedMax = Math.ceil(dataMax) + 1;
    const paddedMin = dataMin < 0 ? Math.floor(dataMin) - 1 : 0;

    return [paddedMin, paddedMax];
  }, [originalChart, data]);

  return (
    <ResponsiveContainer width="100%" height="100%">
      <RechartsBarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: bottomMargin }}>
        <defs>
          {datasetColors.map((color, index) => (
            <linearGradient key={`gradient-${index}`} id={`barGradient-${index}`} x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity={0.8} />
              <stop offset="100%" stopColor={color} stopOpacity={0.3} />
            </linearGradient>
          ))}
        </defs>
        <CartesianGrid strokeDasharray="3 3" />
        <XAxis
          dataKey="name"
          tick={<CustomWrappedTick width={estimatedBarWidth} shouldTilt={shouldTiltLabels} />}
          interval={0}
          height={80}
        />
        <YAxis
          tickFormatter={formatYAxisNumber}
          domain={yAxisDomain}
        />
        <Tooltip content={<CustomTooltip />} />
        {hasNegativeValues && <ReferenceLine y={0} stroke="#666" strokeWidth={2} />}
        {hasMultipleDatasets && <Legend />}
        {(() => {
          if (hasMultipleDatasets && isStacked) {
            // Render stacked bars: gradient for bottom-most, rounding for top-most
            return datasetKeys.map((key: string, index: number) => {
              const isBottomBar = index === 0;
              const isTopBar = index === datasetKeys.length - 1;

              return (
                <Bar
                  key={key}
                  dataKey={key}
                  stackId="a"
                  fill={isBottomBar ? `url(#barGradient-${index % COLORS.length})` : datasetColors[index]}
                  fillOpacity={isBottomBar ? undefined : 0.8}
                  radius={isTopBar ? [8, 8, 0, 0] : undefined}
                />
              );
            });
          } else if (hasMultipleDatasets && !isStacked) {
            // Render grouped bars for multiple datasets (side-by-side) with gradients
            return datasetKeys.map((key: string, index: number) => {
              return (
                <Bar
                  key={key}
                  dataKey={key}
                  fill={`url(#barGradient-${index % COLORS.length})`}
                  shape={RoundedBar}
                />
              );
            });
          } else {
            // Single bar with gradient (original behavior)
            return (
              <Bar dataKey="value" shape={RoundedBar}>
                {data.map((entry: any, i: number) => (
                  <Cell key={i} fill={`url(#barGradient-${i % COLORS.length})`} />
                ))}
              </Bar>
            );
          }
        })()}
      </RechartsBarChart>
    </ResponsiveContainer>
  );
};